import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, ArrowLeft, ArrowRight, Check, Loader2, Users, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import { AUTH } from '../constants/testIds';

const SCHOOL_TYPES = ['Nursery', 'Primary', 'Secondary', 'Nursery & Primary', 'Primary & Secondary', 'Nursery, Primary & Secondary', 'Montessori', 'Tutorial', 'International', 'Faith-based', 'Special'];
const NIGERIAN_STATES = ['Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT - Abuja', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'];

const PLAN_STYLES = {
  gray:   { border: 'border-slate-200', bg: 'bg-slate-50', badge: 'bg-slate-100 text-slate-600', selected: 'border-slate-500 ring-2 ring-slate-200' },
  blue:   { border: 'border-blue-200',  bg: 'bg-blue-50',  badge: 'bg-blue-100 text-blue-700',   selected: 'border-indigo-500 ring-2 ring-indigo-100' },
  purple: { border: 'border-purple-200', bg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700', selected: 'border-purple-500 ring-2 ring-purple-100' },
  green:  { border: 'border-emerald-200', bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700', selected: 'border-emerald-500 ring-2 ring-emerald-100' },
};

const fmt = (n) => n === 0 ? 'Free' : `₦${n.toLocaleString()}`;
const steps = ['Your Account', 'School Info', 'Choose Plan'];

export default function Register() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [billingCycle, setBillingCycle] = useState('term');
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [done, setDone] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || localStorage.getItem('partner_ref_code') || '';

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '',
    school_name: '', school_type: '', state: '', city: '', address: '', country: 'Nigeria',
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Fetch plans when reaching step 3
  useEffect(() => {
    if (step === 3 && plans.length === 0) {
      setPlansLoading(true);
      api.get('/subscriptions/plans')
        .then(res => {
          const p = res.data || [];
          setPlans(p);
          // Default to Starter (free)
          const starter = p.find(x => (x.term_price || x.monthly_price || 0) === 0);
          if (starter) setSelectedPlanId(starter.id);
        })
        .catch(() => toast.error('Could not load plans'))
        .finally(() => setPlansLoading(false));
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateStep1 = () => {
    if (!form.first_name || !form.last_name) { toast.error('Please enter your full name'); return false; }
    if (!form.email || !form.email.includes('@')) { toast.error('Please enter a valid email address'); return false; }
    if (!form.phone) { toast.error('Please enter your phone number'); return false; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return false; }
    return true;
  };

  const validateStep2 = () => {
    if (!form.school_name) { toast.error('Please enter your school name'); return false; }
    if (!form.school_type) { toast.error('Please select school type'); return false; }
    if (!form.state) { toast.error('Please select your state'); return false; }
    if (!form.city) { toast.error('Please enter your city'); return false; }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
    else if (step === 3) handleSubmit();
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const visitorId = localStorage.getItem('partner_visitor_id') || '';
      const payload = {
        ...form,
        plan_id: selectedPlanId,
        ...(refCode ? { referral_code: refCode.toUpperCase(), visitor_id: visitorId } : {}),
      };
      const res = await api.post('/auth/register', payload);
      if (refCode) localStorage.removeItem('partner_ref_code');

      const chosenPlan = plans.find(p => p.id === selectedPlanId);
      const isPaid = chosenPlan && (chosenPlan.term_price || chosenPlan.monthly_price || 0) > 0;

      if (isPaid) {
        // Store token so subscription init can authenticate
        localStorage.setItem('eduva_token', res.token);
        try {
          const callbackUrl = `${window.location.origin}/payment/callback`;
          const payRes = await api.post('/subscriptions/initialize', {
            plan_id: selectedPlanId,
            billing_cycle: billingCycle,
            gateway: 'paystack',
            callback_url: callbackUrl,
          });
          window.location.href = payRes.data.checkout_url;
        } catch (payErr) {
          // Payment init failed — log in and let them retry from Subscription page
          login(res.token, res.user, res.school, res.subscription_status, res.grace_days_remaining);
          toast.error(payErr?.response?.data?.detail || 'Payment gateway not ready. You can subscribe from your dashboard.');
          setTimeout(() => navigate('/school/subscription'), 1800);
        }
      } else {
        login(res.token, res.user, res.school, res.subscription_status, res.grace_days_remaining);
        setDone(true);
        setTimeout(() => navigate('/school/dashboard'), 2200);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#eef1ff] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl p-10 text-center w-full max-w-sm">
        <div className="w-16 h-16 rounded-full bg-[#14C8C4]/20 flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-[#14C8C4]" />
        </div>
        <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-2">School Created!</h2>
        <p className="text-slate-500 mb-2">Welcome to Eduva, <strong>{form.first_name}</strong>!</p>
        <p className="text-slate-400 text-sm">Redirecting to your dashboard...</p>
        <div className="w-6 h-6 border-4 border-[#516EFA] border-t-transparent rounded-full animate-spin mx-auto mt-6" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#eef1ff] flex items-center justify-center p-6">
      <div className="w-full max-w-xl">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-[#516EFA] flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <span className="font-outfit font-bold text-2xl text-slate-900">Eduva</span>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-blue-50 border border-slate-100 p-8">
          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-8">
            {steps.map((s, i) => (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    i + 1 < step ? 'bg-[#14C8C4] text-white' : i + 1 === step ? 'bg-[#516EFA] text-white' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {i + 1 < step ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`text-xs mt-1 ${i + 1 === step ? 'text-[#516EFA] font-medium' : 'text-slate-400'}`}>{s}</span>
                </div>
                {i < steps.length - 1 && <div className={`h-px w-14 mx-3 mb-5 ${i + 1 < step ? 'bg-[#14C8C4]' : 'bg-slate-200'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Referral banner */}
          {refCode && (
            <div className="bg-[#EDF0FF] border border-[#516EFA]/20 rounded-xl px-4 py-3 flex items-center gap-2 mb-4" data-testid="referral-banner">
              <div className="w-6 h-6 rounded-full bg-[#516EFA] flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
              </div>
              <p className="text-xs text-[#516EFA] font-medium">
                You were referred by a partner — code <span className="font-mono font-bold">{refCode}</span>
              </p>
            </div>
          )}

          {/* Step 1 */}
          {step === 1 && (
            <div data-testid={AUTH.registerForm} className="space-y-5">
              <div>
                <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-1">Create your account</h2>
                <p className="text-slate-500 text-sm">You'll be the school owner/administrator</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">First Name</Label>
                  <Input value={form.first_name} onChange={e => update('first_name', e.target.value)} placeholder="John" className="h-11" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Last Name</Label>
                  <Input value={form.last_name} onChange={e => update('last_name', e.target.value)} placeholder="Doe" className="h-11" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Email Address</Label>
                <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="john@school.edu.ng" className="h-11" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone Number</Label>
                <Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+234 801 234 5678" className="h-11" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</Label>
                <Input type="password" value={form.password} onChange={e => update('password', e.target.value)} placeholder="Min. 8 characters" className="h-11" />
              </div>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-1">Tell us about your school</h2>
                <p className="text-slate-500 text-sm">We'll set up your school's workspace</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">School Name</Label>
                <Input value={form.school_name} onChange={e => update('school_name', e.target.value)} placeholder="Greenfield Academy" className="h-11" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">School Type</Label>
                <Select value={form.school_type} onValueChange={v => update('school_type', v)}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Select school type" /></SelectTrigger>
                  <SelectContent>{SCHOOL_TYPES.map(t => <SelectItem key={t} value={t.toLowerCase()}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">State</Label>
                  <Select value={form.state} onValueChange={v => update('state', v)}>
                    <SelectTrigger className="h-11"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent>{NIGERIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">City</Label>
                  <Input value={form.city} onChange={e => update('city', e.target.value)} placeholder="Lagos Island" className="h-11" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Address (Optional)</Label>
                <Input value={form.address} onChange={e => update('address', e.target.value)} placeholder="15 Education Close, Victoria Island" className="h-11" />
              </div>
            </div>
          )}

          {/* Step 3 — Plan Selection */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-1">Choose your plan</h2>
                <p className="text-slate-500 text-sm">You can upgrade anytime from your dashboard</p>
              </div>

              {plansLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
                </div>
              ) : (
                <>
                  <div className="space-y-3" data-testid="plan-selection">
                    {plans.map(plan => {
                      const style = PLAN_STYLES[plan.color] || PLAN_STYLES.blue;
                      const isSelected = selectedPlanId === plan.id;
                      const isExpanded = expandedPlan === plan.id;
                      const isPaidPlan = (plan.term_price || plan.monthly_price || 0) > 0;
                      return (
                        <div
                          key={plan.id}
                          onClick={() => setSelectedPlanId(plan.id)}
                          className={`rounded-xl border-2 cursor-pointer transition-all overflow-hidden
                            ${isSelected ? style.selected : `${style.border} hover:border-slate-300`}`}
                          data-testid={`plan-option-${plan.name.toLowerCase()}`}
                        >
                          <div className={`flex items-center justify-between px-4 py-3 ${style.bg}`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300 bg-white'}`}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900 text-sm">{plan.name}</p>
                                <p className="text-xs text-slate-500">{plan.description}</p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="font-bold text-slate-900">{fmt(plan.term_price || plan.monthly_price || 0)}</p>
                              {isPaidPlan && (
                                <p className="text-[11px] text-slate-400">/term · {plan.term_days || 90} days</p>
                              )}
                            </div>
                          </div>

                          <div className="px-4 py-2 bg-white flex items-center justify-between">
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Up to {plan.student_limit} students</span>
                              <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> {(plan.modules || []).length} modules</span>
                            </div>
                            <button
                              type="button"
                              onClick={e => { e.stopPropagation(); setExpandedPlan(isExpanded ? null : plan.id); }}
                              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
                            >
                              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                              Details
                            </button>
                          </div>

                          {isExpanded && (
                            <div className="px-4 pb-3 bg-white border-t border-slate-100">
                              <div className="grid grid-cols-2 gap-1 pt-2">
                                {(plan.modules || []).map(m => (
                                  <div key={m} className="flex items-center gap-1.5 text-xs text-slate-600">
                                    <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                                    {m.replace(/_/g, ' ')}
                                  </div>
                                ))}
                              </div>
                              {(plan.session_price || plan.yearly_price || 0) > 0 && (
                                <p className="text-xs text-indigo-600 mt-2 font-medium">
                                  Full session: {fmt(plan.session_price || plan.yearly_price || 0)} for {plan.session_days || 270} days
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Billing cycle toggle — shown only when a paid plan is selected */}
                  {(() => {
                    const chosenPlan = plans.find(p => p.id === selectedPlanId);
                    const isPaid = chosenPlan && (chosenPlan.term_price || chosenPlan.monthly_price || 0) > 0;
                    if (!isPaid) return null;
                    const termAmt = chosenPlan.term_price || chosenPlan.monthly_price || 0;
                    const sessAmt = chosenPlan.session_price || chosenPlan.yearly_price || 0;
                    return (
                      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 space-y-2" data-testid="billing-cycle-toggle">
                        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Billing Period</p>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { id: 'term', label: 'Per Term', price: termAmt, note: `${chosenPlan.term_days || 90} days access` },
                            { id: 'session', label: 'Full Session', price: sessAmt || termAmt * 3, note: `${chosenPlan.session_days || 270} days access` },
                          ].map(opt => (
                            <button key={opt.id} type="button" onClick={() => setBillingCycle(opt.id)}
                              className={`p-3 rounded-lg border text-left transition-all ${billingCycle === opt.id ? 'border-indigo-500 bg-white shadow-sm' : 'border-slate-200 bg-white/50 hover:bg-white'}`}
                              data-testid={`reg-billing-${opt.id}`}>
                              <p className="text-xs font-bold text-slate-800">{opt.label}</p>
                              <p className="text-sm font-bold text-indigo-600 mt-0.5">{fmt(opt.price)}</p>
                              <p className="text-[10px] text-slate-400 mt-0.5">{opt.note}</p>
                            </button>
                          ))}
                        </div>
                        <p className="text-[11px] text-slate-500 flex items-center gap-1">
                          <span className="text-amber-500">→</span> You'll be redirected to a secure payment page after registration
                        </p>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(s => s - 1)} className="flex-1 h-11">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            )}
            <Button onClick={handleNext} disabled={loading || (step === 3 && plansLoading)}
              data-testid={step === 3 ? AUTH.registerSubmit : 'register-next-btn'}
              className="flex-1 h-11 bg-[#516EFA] hover:bg-[#4058e8] text-white font-semibold rounded-xl">
              {loading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {step === 3 && (() => { const cp = plans.find(p => p.id === selectedPlanId); return cp && (cp.term_price || cp.monthly_price || 0) > 0; })() ? 'Redirecting to Payment...' : 'Creating School...'}</>
                : step === 3
                  ? (() => {
                      const cp = plans.find(p => p.id === selectedPlanId);
                      const isPaidStep = cp && (cp.term_price || cp.monthly_price || 0) > 0;
                      return isPaidStep
                        ? <><ArrowRight className="w-4 h-4 mr-2" /> Continue to Payment</>
                        : <><Check className="w-4 h-4 mr-2" /> Create School</>;
                    })()
                  : <> Next <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-[#516EFA] font-semibold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
